import AdValidator from 'lib/AdValidator';
import {
  UPDATE_FIELDS,
} from 'actions/editable-ad';
import {
  VALIDATE_FIELDS,
  VALIDATE_STEP_FIELDS,
} from 'actions/validation';
import {
  lang,
  UPLOADED_AUDIO,
  EXISTING_VO_BG_MUSIC,
  BG_MUSIC,
  UPLOADED_COVER_ART,
  FILE_PROPERTIES,
  EXISTING_VOICEOVER,
  NEW_VOICEOVER,
  DEFAULT_AUDIO,
  CLIENT_AUDIO_TYPES,
  CLIENT_IMAGE_TYPES,
  MAX_FILE_SIZE_LIMIT,
  MAX_AUDIO_DURATION,
  COVER_ART_IMAGE_DIMENSIONS,
} from 'config';
import { fillObject } from 'utilities';

export const ADCREATION_RULES = {
  clickthroughUrl: `required|url`,
  name: 'required',
  'uploads.image.uploaded': 'accepted',
};

const BASE_VALIDATION_RULES = {
  ...ADCREATION_RULES,
};

const NEW_VOICEOVER_RULES = {
  [`audioSubmission.${NEW_VOICEOVER}.name`]: 'required',
  [`audioSubmission.${NEW_VOICEOVER}.instructions`]: 'required',
  [`audioSubmission.${NEW_VOICEOVER}.locale`]: 'required',
  [`audioSubmission.${NEW_VOICEOVER}.transcript`]: 'required',
  [`audioSubmission.${NEW_VOICEOVER}.voice`]: 'required',
};

export const GENERIC_AUDIO_RULES = `file-problem|filetype:${CLIENT_AUDIO_TYPES.join(',')}|filesize-max:${MAX_FILE_SIZE_LIMIT}`;

function addAudioRules(audioType, rules, maxDuration) {
  rules[`uploads.${audioType}.uploaded`] = 'accepted';
  rules[`uploads.${audioType}.${FILE_PROPERTIES}`] =
    `${GENERIC_AUDIO_RULES}${maxDuration ? '|audio-duration:' + maxDuration : ''}`;
}

export const UPLOADED_AUDIO_RULES = {};
addAudioRules(UPLOADED_AUDIO, UPLOADED_AUDIO_RULES, MAX_AUDIO_DURATION);

export const EXISTING_VO_BG_RULES = {};
addAudioRules(EXISTING_VO_BG_MUSIC, EXISTING_VO_BG_RULES);

export const BG_MUSIC_RULES = {};
addAudioRules(BG_MUSIC, BG_MUSIC_RULES);

export const ALL_AUDIO_RULES = {
  ...UPLOADED_AUDIO_RULES,
  ...EXISTING_VO_BG_RULES,
  ...BG_MUSIC_RULES,
};

const EXISTING_VOICEOVER_RULES = {
  [`audioSubmission.${EXISTING_VOICEOVER}.data.id`]: 'required',
};

function getRulesFromActiveAudioSubmission(activeSubmission) {
  switch (activeSubmission) {
    case UPLOADED_AUDIO:
      return { ...UPLOADED_AUDIO_RULES };
    case EXISTING_VOICEOVER:
      return { ...EXISTING_VOICEOVER_RULES };
    case NEW_VOICEOVER:
      return { ...NEW_VOICEOVER_RULES };
    default:
      return null;
  }
}

const validImageDimensions = `${COVER_ART_IMAGE_DIMENSIONS.width}x${COVER_ART_IMAGE_DIMENSIONS.height}`;

export const UPLOADED_IMAGE_RULES = {
  [`uploads.${UPLOADED_COVER_ART}.uploaded`]: 'accepted',
  [`uploads.${UPLOADED_COVER_ART}.${FILE_PROPERTIES}`]:
  `file-problem|filetype:${CLIENT_IMAGE_TYPES.join(',')}|image-dimensions:${validImageDimensions}`,
};

export const STEP_RULES = {
  3: fields => ({
    ...ADCREATION_RULES,
    ...getRulesFromActiveAudioSubmission(fields.audioSubmission.active),
  }),
};

const TOTAL_VALIDATION_RULES = {
  ...BASE_VALIDATION_RULES,
  ...NEW_VOICEOVER_RULES,
  ...EXISTING_VOICEOVER_RULES,
  ...UPLOADED_AUDIO_RULES,
  ...UPLOADED_IMAGE_RULES,
  ...EXISTING_VO_BG_RULES,
  ...BG_MUSIC_RULES,
};

AdValidator.CUSTOM_ERR_MSGS = {
  'accepted.uploads.image.uploaded': lang('missing-image-file', 'An image is required.'),
  'required.targeting.locations': lang('missing-location', 'Please select at least one location.'),
  required: {
    string: lang('missing-field', 'This field is required.'),
  },
  'required.workingFor': {
    string: 'Please select an option.',
  },
  accepted: {
    string: lang('missing-field', 'This field is required.'),
  },
  'required.payments.totalBudget': lang('missing-required-total-budget', 'A total budget must be selected.'),
  'email.businessEmail': { string: 'Invalid email address.' },
};

const initialValidation = {
  isValid: false,
  errorMsg: '',
  touched: false,
};

export function initialState({ stepRules, totalRules } = { stepRules: STEP_RULES, totalRules: TOTAL_VALIDATION_RULES }) {
  const state = {
    allFieldsValid: false,
    'audioSubmission.active': DEFAULT_AUDIO,
    ...Object.keys(totalRules).reduce((result, field) => {
      result[field] = initialValidation;
      return result;
    }, {}),
  };

  if (stepRules) {
    state.steps = fillObject(Object.keys(stepRules), {
      isValid: false,
      touched: false,
    });
  }

  return state;
}

function validateAllFields(state) {
  const typeToRule = {
    [NEW_VOICEOVER]: NEW_VOICEOVER_RULES,
    [EXISTING_VOICEOVER]: EXISTING_VOICEOVER_RULES,
    [UPLOADED_AUDIO]: UPLOADED_AUDIO_RULES,
  };
  const activeAudioType = state['audioSubmission.active'];

  const activeAudioTypeRulesValid = !Object.keys(state).filter(k => typeToRule[activeAudioType][k] !== undefined).some(x => state[x].isValid === false);
  const baseRulesValid = !Object.keys(state).filter(k => BASE_VALIDATION_RULES[k] !== undefined).some(x => state[x].isValid === false);

  return activeAudioTypeRulesValid && baseRulesValid;
}

// Helpers
export function fieldsAreValid(step, editableAd, validationFields) {
  const stepRules = STEP_RULES[step](editableAd);
  return Object.keys(stepRules).every(k => validationFields[k].isValid);
}

export default function validation(state = initialState(), action) {
  let data;
  let rules;
  let errors;
  let validator;
  let newState;

  switch (action.type) {
    case UPDATE_FIELDS:
      // Change active audio type
      if (action.field.key === 'audioSubmission.active') {
        newState = {
          ...state,
          'audioSubmission.active': action.field.value,
        };

        return {
          ...newState,
          allFieldsValid: validateAllFields(newState, action.mode),
        };
      }

      if (!action.field.shouldValidate || !TOTAL_VALIDATION_RULES[action.field.key]) {
        return state;
      }

      data = { [action.field.key]: action.field.value };
      rules = { [action.field.key]: TOTAL_VALIDATION_RULES[action.field.key] };
      validator = new AdValidator(data, rules, action.groups);

      newState = {
        ...state,
        [action.field.key]: {
          isValid: validator.check(),
          errorMsg: validator.fails() ? validator.errors.first(action.field.key) : '',
          touched: true,
        },
      };

      return {
        ...newState,
        allFieldsValid: validateAllFields(newState, action.mode),
      };
    case VALIDATE_STEP_FIELDS:
      rules = STEP_RULES[action.step](action.fields);
      validator = new AdValidator(action.fields, rules, action.groups);
      validator.check();

      errors = validator.errors.all();

      newState = {
        ...state,
        steps: {
          ...state.steps,
          [action.step]: {
            isValid: validator.passes(),
            touched: true,
          },
        },
        ...Object.keys(errors).reduce((result, fieldKey) => {
          result[fieldKey] = {
            isValid: false,
            errorMsg: errors[fieldKey][0],
            touched: true,
          };
          return result;
        }, {}),
      };

      return {
        ...newState,
        allFieldsValid: validateAllFields(newState),
      };
    case VALIDATE_FIELDS:
      rules = {
        ...BASE_VALIDATION_RULES,
        ...getRulesFromActiveAudioSubmission(action.fields.audioSubmission.active),
      };
      validator = new AdValidator(action.fields, rules, action.groups);
      validator.check();

      errors = validator.errors.all();

      newState = {
        ...state,
        'audioSubmission.active': action.fields.audioSubmission.active,
        ...Object.keys(TOTAL_VALIDATION_RULES).reduce((result, fieldKey) => {
          result[fieldKey] = {
            isValid: !errors[fieldKey],
            errorMsg: !!errors[fieldKey] && errors[fieldKey][0] || '',
            touched: !action.initialValidation,
          };
          return result;
        }, {}),
      };

      return {
        ...newState,
        allFieldsValid: validator.errorCount === 0,
        steps: Object.keys(state.steps).reduce((result, stepNumber) => {
          result[stepNumber] = {
            isValid: fieldsAreValid(stepNumber, action.fields, newState),
            touched: !action.initialValidation,
          };
          return result;
        }, {}),
      };
    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/validation.js